/**
 * Floating browser preview state, keyed per thread.
 *
 * Closing the browser panel on a live session floats it instead of dropping
 * it. This store holds where the floater sits and how wide the user made it.
 * One source per thread: opening a new tab replaces the floater but keeps its
 * position and width.
 *
 * Ported from t3code (`apps/web/src/previewMiniPlayerStore.ts`, MIT, pin
 * `e3e7cc3f`). Adaptations: forge contracts (`ScopedThreadRef`,
 * `scopedThreadKey`); upstream's device-stream source kind is not ported
 * because forge has no device streaming; the auto-show suppression record is
 * forge's own, backing the adapted automation behavior that re-floats the
 * player when the agent drives while the panel is closed.
 *
 * @module browserMiniPlayerStore
 */
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import { create } from "zustand";

import type { RightPanelSurface } from "~/rightPanelSurface";

export interface BrowserMiniPlayerPosition {
  readonly x: number;
  readonly y: number;
}

export interface BrowserMiniPlayerSize {
  readonly width: number;
  readonly height: number;
}

/** What the floating player mirrors: a browser tab of the thread's session. */
export interface BrowserMiniPlayerSource {
  readonly kind: "browser";
  readonly tabId: string;
}

export interface BrowserMiniPlayerState {
  readonly source: BrowserMiniPlayerSource;
  readonly position: BrowserMiniPlayerPosition | null;
  /** Height always follows the mirrored source's aspect ratio. */
  readonly width: number | null;
}

interface BrowserMiniPlayerStoreState {
  readonly byThreadKey: Record<string, BrowserMiniPlayerState>;
  /**
   * Threads where a floated player was dismissed, holding the source key it
   * was dismissed on. Stops agent-driven auto-show from fighting the user
   * who just closed it; a different tab floats again.
   */
  readonly autoShowSuppressedByThreadKey: Record<string, string>;
  readonly open: (ref: ScopedThreadRef, source: BrowserMiniPlayerSource) => void;
  readonly close: (ref: ScopedThreadRef) => void;
  /** The user's dismissal: drops the floater and suppresses auto-show for it. */
  readonly dismiss: (ref: ScopedThreadRef) => void;
  /** `sourceKey` guards against a drag that outlives the source it started on. */
  readonly move: (
    ref: ScopedThreadRef,
    sourceKey: string,
    position: BrowserMiniPlayerPosition,
  ) => void;
  readonly resize: (ref: ScopedThreadRef, sourceKey: string, width: number) => void;
  readonly removeThread: (ref: ScopedThreadRef) => void;
  readonly clearAutoShowSuppression: (ref: ScopedThreadRef) => void;
}

export function browserMiniPlayerSourceKey(source: BrowserMiniPlayerSource): string {
  return `browser:${source.tabId}`;
}

export const browserMiniPlayerSource = (tabId: string): BrowserMiniPlayerSource => ({
  kind: "browser",
  tabId,
});

export const useBrowserMiniPlayerStore = create<BrowserMiniPlayerStoreState>()((set) => ({
  byThreadKey: {},
  autoShowSuppressedByThreadKey: {},
  open: (ref, source) =>
    set((state) => {
      const threadKey = scopedThreadKey(ref);
      const current = state.byThreadKey[threadKey];
      const { [threadKey]: _cleared, ...autoShowSuppressedByThreadKey } =
        state.autoShowSuppressedByThreadKey;
      if (
        current &&
        browserMiniPlayerSourceKey(current.source) === browserMiniPlayerSourceKey(source)
      ) {
        return { autoShowSuppressedByThreadKey };
      }
      return {
        autoShowSuppressedByThreadKey,
        byThreadKey: {
          ...state.byThreadKey,
          [threadKey]: {
            source,
            position: current?.position ?? null,
            width: current?.width ?? null,
          },
        },
      };
    }),
  close: (ref) =>
    set((state) => {
      const threadKey = scopedThreadKey(ref);
      if (!(threadKey in state.byThreadKey)) return state;
      const { [threadKey]: _closed, ...byThreadKey } = state.byThreadKey;
      return { byThreadKey };
    }),
  dismiss: (ref) =>
    set((state) => {
      const threadKey = scopedThreadKey(ref);
      const current = state.byThreadKey[threadKey];
      if (!current) return state;
      const { [threadKey]: _closed, ...byThreadKey } = state.byThreadKey;
      return {
        byThreadKey,
        autoShowSuppressedByThreadKey: {
          ...state.autoShowSuppressedByThreadKey,
          [threadKey]: browserMiniPlayerSourceKey(current.source),
        },
      };
    }),
  move: (ref, sourceKey, position) =>
    set((state) => {
      const threadKey = scopedThreadKey(ref);
      const current = state.byThreadKey[threadKey];
      if (!current || browserMiniPlayerSourceKey(current.source) !== sourceKey) return state;
      if (current.position?.x === position.x && current.position.y === position.y) return state;
      return {
        byThreadKey: {
          ...state.byThreadKey,
          [threadKey]: { ...current, position },
        },
      };
    }),
  resize: (ref, sourceKey, width) =>
    set((state) => {
      const threadKey = scopedThreadKey(ref);
      const current = state.byThreadKey[threadKey];
      if (
        !current ||
        browserMiniPlayerSourceKey(current.source) !== sourceKey ||
        current.width === width
      ) {
        return state;
      }
      return {
        byThreadKey: {
          ...state.byThreadKey,
          [threadKey]: { ...current, width },
        },
      };
    }),
  removeThread: (ref) =>
    set((state) => {
      const threadKey = scopedThreadKey(ref);
      if (
        !(threadKey in state.byThreadKey) &&
        !(threadKey in state.autoShowSuppressedByThreadKey)
      ) {
        return state;
      }
      const { [threadKey]: _removed, ...byThreadKey } = state.byThreadKey;
      const { [threadKey]: _suppression, ...autoShowSuppressedByThreadKey } =
        state.autoShowSuppressedByThreadKey;
      return { byThreadKey, autoShowSuppressedByThreadKey };
    }),
  clearAutoShowSuppression: (ref) =>
    set((state) => {
      const threadKey = scopedThreadKey(ref);
      if (!(threadKey in state.autoShowSuppressedByThreadKey)) return state;
      const { [threadKey]: _cleared, ...autoShowSuppressedByThreadKey } =
        state.autoShowSuppressedByThreadKey;
      return { autoShowSuppressedByThreadKey };
    }),
}));

export function selectThreadBrowserMiniPlayer(
  byThreadKey: Record<string, BrowserMiniPlayerState>,
  ref: ScopedThreadRef | null | undefined,
): BrowserMiniPlayerState | null {
  if (!ref) return null;
  return byThreadKey[scopedThreadKey(ref)] ?? null;
}

/** The floating browser tab, or null when nothing floats. */
export function selectThreadBrowserMiniPlayerTabId(
  byThreadKey: Record<string, BrowserMiniPlayerState>,
  ref: ScopedThreadRef | null | undefined,
): string | null {
  return selectThreadBrowserMiniPlayer(byThreadKey, ref)?.source.tabId ?? null;
}

/** Whether the thread has an agent-driven auto-show suppressed on this source. */
export function selectBrowserMiniPlayerAutoShowSuppressed(
  autoShowSuppressedByThreadKey: Record<string, string>,
  ref: ScopedThreadRef | null | undefined,
  sourceKey: string,
): boolean {
  if (!ref) return false;
  return autoShowSuppressedByThreadKey[scopedThreadKey(ref)] === sourceKey;
}

/** The floating player hides only while the same tab is rendered in the panel. */
export function shouldRenderBrowserMiniPlayer(
  source: BrowserMiniPlayerSource | null,
  renderedRightPanelSurface: RightPanelSurface | null,
): boolean {
  if (source === null) return false;
  return !(
    renderedRightPanelSurface?.kind === "preview" &&
    renderedRightPanelSurface.resourceId === source.tabId
  );
}
